'use client';
import { useState, useEffect } from 'react';
import { RatingDetailsSidebar } from '@/_components/products/RatingDetailsSidebar';

const STAR_COLOR = '#f59e0b';
const TEAL = '#1bb8a7';

const DistBar = ({ star, count, total }) => {
    const pct   = total > 0 ? Math.round((count / total) * 100) : 0;
    const color = star >= 4 ? '#10b981' : star === 3 ? '#f59e0b' : '#ef4444';
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, width: 30, flexShrink: 0 }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>{star}</span>
                <i className="pi pi-star-fill" style={{ fontSize: '0.5rem', color: STAR_COLOR }} />
            </div>
            <div style={{ flex: 1, height: 8, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{
                    height: '100%', width: `${pct}%`, background: color,
                    borderRadius: 99, transition: 'width 0.7s cubic-bezier(.4,0,.2,1)',
                }} />
            </div>
            <span style={{ fontSize: '0.68rem', color: '#94a3b8', width: 32, textAlign: 'right', flexShrink: 0 }}>
                {pct}%
            </span>
        </div>
    );
};

export const ProductRatingSection = ({ product }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [data,        setData]        = useState(null);

    const productId   = product?._id;
    const avgRating   = data?.summary?.avgRating   ?? product?.averageRating ?? 0;
    const reviewCount = data?.summary?.totalReviews ?? product?.reviewCount  ?? 0;

    useEffect(() => {
        if (!productId) return;
        fetch(`/api/review/${productId}?limit=50`)
            .then(r => r.json())
            .then(json => setData(json?.data || null))
            .catch(() => {});
    }, [productId]);

    const reviews = data?.reviews || [];
    const dist    = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(r => { if (dist[r.rating] !== undefined) dist[r.rating]++; });
    const total = reviews.length;

    if (!avgRating && !reviewCount) return null;

    return (
        <>
            <RatingDetailsSidebar
                product={product}
                visible={sidebarOpen}
                onHide={() => setSidebarOpen(false)}
            />

            <div style={{
                borderRadius: 16,
                border: `1px solid rgba(27,184,167,0.25)`,
                background: 'linear-gradient(135deg, #f0fdfa 0%, #fff 80%)',
                padding: '20px 24px',
                boxShadow: '0 4px 24px rgba(27,184,167,0.1)',
            }}>
                {/* Section title */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: 10,
                            background: `linear-gradient(135deg, ${TEAL}, #1bb8a7)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: `0 4px 14px ${TEAL}44`,
                            flexShrink: 0,
                        }}>
                            <i className="pi pi-star-fill" style={{ fontSize: '0.9rem', color: '#fff' }} />
                        </div>
                        <div>
                            <p style={{ margin: 0, fontWeight: 800, fontSize: '1rem', color: TEAL }}>
                                Ratings & Reviews
                            </p>
                            {reviewCount > 0 && (
                                <p style={{ margin: 0, fontSize: '0.68rem', color: '#94a3b8' }}>
                                    Based on {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
                                </p>
                            )}
                        </div>
                    </div>

                    <button
                        onClick={() => setSidebarOpen(true)}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '8px 18px', borderRadius: 10,
                            background: `linear-gradient(135deg, ${TEAL}, #0f766e)`,
                            border: 'none', color: '#fff',
                            fontSize: '0.8rem', fontWeight: 700,
                            cursor: 'pointer',
                            boxShadow: `0 4px 16px ${TEAL}44`,
                            transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.boxShadow = `0 6px 22px ${TEAL}66`}
                        onMouseLeave={e => e.currentTarget.style.boxShadow = `0 4px 16px ${TEAL}44`}
                    >
                        <i className="pi pi-list" style={{ fontSize: '0.72rem' }} />
                        View All Reviews
                    </button>
                </div>

                {/* Main content row */}
                <div style={{ display: 'flex', gap: 28, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Big avg + stars */}
                    <div style={{ textAlign: 'center', flexShrink: 0 }}>
                        <div style={{
                            fontSize: '3.6rem', fontWeight: 900, color: TEAL,
                            lineHeight: 1, letterSpacing: '-3px',
                        }}>
                            {Number(avgRating).toFixed(1)}
                        </div>
                        <div style={{ display: 'flex', gap: 3, justifyContent: 'center', marginTop: 6 }}>
                            {[1,2,3,4,5].map(s => (
                                <i key={s}
                                    className={`pi ${s <= Math.round(avgRating) ? 'pi-star-fill' : 'pi-star'}`}
                                    style={{ fontSize: '0.9rem', color: s <= Math.round(avgRating) ? STAR_COLOR : '#d1d5db' }}
                                />
                            ))}
                        </div>
                        <p style={{ margin: '5px 0 0', fontSize: '0.68rem', color: '#94a3b8' }}>
                            out of 5
                        </p>
                    </div>

                    {/* Divider */}
                    <div style={{ width: 1, height: 90, background: '#e2e8f0', flexShrink: 0 }} />

                    {/* Distribution bars */}
                    <div style={{ flex: 1, minWidth: 180, display: 'flex', flexDirection: 'column', gap: 7 }}>
                        {[5,4,3,2,1].map(s => (
                            <DistBar key={s} star={s} count={dist[s]} total={total} />
                        ))}
                    </div>

                    {/* Divider */}
                    <div style={{ width: 1, height: 90, background: '#e2e8f0', flexShrink: 0, display: 'none' }} className="lg:block" />

                    {/* Summary badges */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '10px 16px', borderRadius: 12,
                            background: `${TEAL}12`, border: `1px solid ${TEAL}30`,
                        }}>
                            <i className="pi pi-star-fill" style={{ color: STAR_COLOR, fontSize: '1rem' }} />
                            <div>
                                <p style={{ margin: 0, fontWeight: 800, fontSize: '1.1rem', color: TEAL }}>
                                    {Number(avgRating).toFixed(1)}
                                </p>
                                <p style={{ margin: 0, fontSize: '0.62rem', color: '#64748b' }}>Avg Rating</p>
                            </div>
                        </div>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '10px 16px', borderRadius: 12,
                            background: '#f8fafc', border: '1px solid #e2e8f0',
                        }}>
                            <i className="pi pi-users" style={{ color: '#6366f1', fontSize: '1rem' }} />
                            <div>
                                <p style={{ margin: 0, fontWeight: 800, fontSize: '1.1rem', color: '#1e293b' }}>
                                    {reviewCount > 999 ? `${(reviewCount / 1000).toFixed(1)}k` : reviewCount}
                                </p>
                                <p style={{ margin: 0, fontSize: '0.62rem', color: '#64748b' }}>Reviews</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Click CTA */}
                <button
                    onClick={() => setSidebarOpen(true)}
                    style={{
                        marginTop: 18, width: '100%',
                        padding: '10px', borderRadius: 11,
                        background: `${TEAL}10`, border: `1px dashed ${TEAL}50`,
                        color: TEAL, fontSize: '0.78rem', fontWeight: 600,
                        cursor: 'pointer', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: 7,
                        transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = `${TEAL}18`; e.currentTarget.style.borderStyle = 'solid'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = `${TEAL}10`; e.currentTarget.style.borderStyle = 'dashed'; }}
                >
                    <i className="pi pi-comments" style={{ fontSize: '0.75rem' }} />
                    Read all customer reviews
                    <i className="pi pi-arrow-right" style={{ fontSize: '0.65rem' }} />
                </button>
            </div>
        </>
    );
};
